import type { CreativeImportPreviewDto, CreativeImportRowDto } from "@nexus/contracts";
import { rowIsInProduction } from "./import-row-status";

export function normalizeImportPreview(
  preview: CreativeImportPreviewDto,
): CreativeImportPreviewDto {
  return {
    ...preview,
    warningIssueCount:
      preview.warningIssueCount ??
      preview.issues.filter((issue) => issue.severity === "WARNING").length,
    warningsAcknowledgedAt: preview.warningsAcknowledgedAt ?? null,
  };
}

export interface VendorLabelGroup {
  label: string;
  rowCount: number;
  mappedVendorId: string | null;
  mappedVendorName: string | null;
  pendingVendorContactEmail: string | null;
}

export type ImportWorkflowStep = "import" | "vendors" | "dates" | "issues" | "finish";

export interface ImportReadiness {
  steps: Record<ImportWorkflowStep, boolean>;
  activeStep: ImportWorkflowStep;
  unmappedVendorLabels: number;
  unmappedDirectRows: number;
  rowsWithPrefilledDates: number;
  rowsMissingProductionDates: number;
  warningIssueCount: number;
  warningsAcknowledged: boolean;
  rowsNeedingAttention: number;
  blockingIssueCount: number;
  canCommit: boolean;
}

function rowNeedsProductionDates(row: CreativeImportRowDto): boolean {
  return row.willCommit && rowIsInProduction(row);
}

export function buildVendorLabelGroups(rows: CreativeImportRowDto[]): VendorLabelGroup[] {
  const map = new Map<string, VendorLabelGroup>();
  for (const row of rows) {
    const trimmed = row.vendorLabel?.trim();
    if (!trimmed) continue;
    const existing = map.get(trimmed);
    if (!existing) {
      map.set(trimmed, {
        label: trimmed,
        rowCount: 1,
        mappedVendorId: row.vendorId,
        mappedVendorName: row.vendorName,
        pendingVendorContactEmail: row.vendorId ? null : row.pendingVendorContactEmail,
      });
      continue;
    }
    existing.rowCount += 1;
    if (row.vendorId) {
      if (!existing.mappedVendorId) {
        existing.mappedVendorId = row.vendorId;
        existing.mappedVendorName = row.vendorName;
      } else if (existing.mappedVendorId !== row.vendorId) {
        existing.mappedVendorId = null;
        existing.mappedVendorName = null;
      }
      existing.pendingVendorContactEmail = null;
    } else if (existing.mappedVendorId) {
      existing.mappedVendorId = null;
      existing.mappedVendorName = null;
    }
    if (!row.vendorId && row.pendingVendorContactEmail && !existing.pendingVendorContactEmail) {
      existing.pendingVendorContactEmail = row.pendingVendorContactEmail;
    } else if (
      existing.pendingVendorContactEmail &&
      row.pendingVendorContactEmail &&
      existing.pendingVendorContactEmail !== row.pendingVendorContactEmail
    ) {
      existing.pendingVendorContactEmail = null;
    }
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
}

function isVendorGroupMapped(group: VendorLabelGroup): boolean {
  return Boolean(group.mappedVendorId || group.pendingVendorContactEmail);
}

/** Blocking issues on rows that will commit — archive rows must not block finish. */
export function countBlockingIssuesOnCommittingRows(
  preview: CreativeImportPreviewDto | undefined,
): number {
  if (!preview) return 0;
  const committingRowIds = new Set(
    preview.rows.filter((row) => row.willCommit).map((row) => row.id),
  );
  return preview.issues.filter(
    (issue) =>
      issue.severity === "BLOCKING" &&
      issue.rowId !== null &&
      committingRowIds.has(issue.rowId),
  ).length;
}

export function filterIssuesOnCommittingRows<T extends { rowId: string | null }>(
  preview: CreativeImportPreviewDto | undefined,
  issues: T[],
): T[] {
  if (!preview) return [];
  const committingRowIds = new Set(
    preview.rows.filter((row) => row.willCommit).map((row) => row.id),
  );
  return issues.filter(
    (issue) => issue.rowId !== null && committingRowIds.has(issue.rowId),
  );
}

export function computeImportReadiness(
  preview: CreativeImportPreviewDto | undefined,
  vendorLabelGroups: VendorLabelGroup[],
): ImportReadiness {
  const rows = preview?.rows ?? [];
  const unmappedVendorLabels = vendorLabelGroups.filter((group) => !isVendorGroupMapped(group)).length;
  const unmappedDirectRows = rows.filter(
    (row) => !row.vendorLabel?.trim() && !row.vendorId,
  ).length;
  const rowsWithPrefilledDates = rows.filter(
    (row) => rowNeedsProductionDates(row) && row.clientStartDatePrefilled,
  ).length;
  const rowsMissingProductionDates = rows.filter(
    (row) =>
      rowNeedsProductionDates(row) &&
      (!row.clientStartDate || !row.expectedDeliveryDate || row.clientStartDatePrefilled),
  ).length;
  const blockingIssueCount = countBlockingIssuesOnCommittingRows(preview);
  const hasBlockingIssues = blockingIssueCount > 0;
  const warningIssueCount = preview?.warningIssueCount ?? 0;
  const warningsAcknowledged = Boolean(preview?.warningsAcknowledgedAt);

  const vendorsComplete =
    Boolean(preview) && unmappedVendorLabels === 0 && unmappedDirectRows === 0;
  const datesComplete = vendorsComplete && rowsMissingProductionDates === 0;
  const issuesComplete =
    datesComplete && !hasBlockingIssues && (warningsAcknowledged || warningIssueCount === 0);
  const canFinish =
    issuesComplete && (preview?.committingRowCount ?? 0) > 0;

  const steps: Record<ImportWorkflowStep, boolean> = {
    import: Boolean(preview),
    vendors: vendorsComplete,
    dates: datesComplete,
    issues: issuesComplete,
    finish: canFinish,
  };

  const activeStep: ImportWorkflowStep = !steps.import
    ? "import"
    : !steps.vendors
      ? "vendors"
      : !steps.dates
        ? "dates"
        : !steps.issues
          ? "issues"
          : "finish";

  const rowsNeedingAttention = rows.filter((row) => rowNeedsAttention(row, vendorLabelGroups)).length;

  return {
    steps,
    activeStep,
    unmappedVendorLabels,
    unmappedDirectRows,
    rowsWithPrefilledDates,
    rowsMissingProductionDates,
    warningIssueCount,
    warningsAcknowledged,
    rowsNeedingAttention,
    blockingIssueCount,
    canCommit: canFinish,
  };
}

export function rowNeedsAttention(
  row: CreativeImportRowDto,
  _vendorLabelGroups: VendorLabelGroup[],
): boolean {
  if (!row.willCommit) return false;
  if (rowIsInProduction(row)) {
    if (!row.clientStartDate) return true;
    if (!row.expectedDeliveryDate) return true;
    if (row.clientStartDatePrefilled) return true;
  }
  return false;
}

export type RowFilter = "all" | "needsAttention" | "ready" | "archive";

export function filterImportRows(
  rows: CreativeImportRowDto[],
  filter: RowFilter,
  vendorLabelGroups: VendorLabelGroup[],
): CreativeImportRowDto[] {
  switch (filter) {
    case "needsAttention":
      return rows.filter((row) => rowNeedsAttention(row, vendorLabelGroups));
    case "ready":
      return rows.filter(
        (row) => row.willCommit && !rowNeedsAttention(row, vendorLabelGroups),
      );
    case "archive":
      return rows.filter((row) => !row.willCommit);
    default:
      return rows;
  }
}
